import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per volleyball match
 * {
 *   matchType: "best_of_3" | "best_of_5",
 *   team1Name: string,
 *   team2Name: string,
 *   team1Players: [{ id, userId, name, isCustom }],
 *   team2Players: [{ id, userId, name, isCustom }],
 *   sets: [{ setNumber, team1Points, team2Points, winner }],
 *   pointEvents: [
 *     { id, team: "team1" | "team2", player, playerId, action, setNumber }
 *   ],
 *   substitutions: [
 *     { id, team: "team1" | "team2", playerOut, playerOutId, playerIn, playerInId, setNumber }
 *   ],
 *   userSets: number,
 *   opponentSets: number,
 *   winner: "team1" | "team2" | null
 * }
 */

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

export const AddVolleyball = () => {
  const location = useLocation();
  const data = location.state || {};

  const [volleyballMatches, setVolleyballMatches] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  // Tailwind class helpers for consistent styling
  const brandBlue = "#3498db";
  const brandBlueDark = "#2980b9";

  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noMatchesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
  };

  const validateMatches = () => {
    const errors = [];

    volleyballMatches.forEach((match, index) => {
      // Check team names
      if (!match.userTeamName || !match.opponentTeamName) {
        errors.push(`Match ${index + 1}: Team names are required`);
      }

      // Check if teams have players
      if (match.userTeam.length === 0 || match.opponentTeam.length === 0) {
        errors.push(
          `Match ${index + 1}: Each team must have at least one player`
        );
      }

      // Check for minimum team size (at least 6 players for volleyball)
      if (match.userTeam.length < 6) {
        errors.push(
          `Match ${
            index + 1
          }: Your team should have at least 6 players for a proper volleyball match`
        );
      }

      if (match.opponentTeam.length < 6) {
        errors.push(
          `Match ${
            index + 1
          }: Opponent team should have at least 6 players for a proper volleyball match`
        );
      }

      // Check for libero count (max 2 per team)
      const userLiberos = match.userTeam.filter(
        (player) => player.isLibero
      ).length;
      const opponentLiberos = match.opponentTeam.filter(
        (player) => player.isLibero
      ).length;

      if (userLiberos > 2) {
        errors.push(
          `Match ${index + 1}: Your team cannot have more than 2 liberos`
        );
      }

      if (opponentLiberos > 2) {
        errors.push(
          `Match ${index + 1}: Opponent team cannot have more than 2 liberos`
        );
      }

      // Validate set scores
      match.sets.forEach((set, setIndex) => {
        if (set.userTeamPoints < 0 || set.opponentTeamPoints < 0) {
          errors.push(
            `Match ${index + 1}, Set ${setIndex + 1}: Points cannot be negative`
          );
        }

        // Check for reasonable set scores (volleyball sets typically go to 25 points, deciding set to 15)
        const isDecidingSet =
          (match.matchType === "best_of_3" && setIndex === 2) ||
          (match.matchType === "best_of_5" && setIndex === 4);
        const maxPoints = isDecidingSet ? 15 : 25;

        if (
          set.userTeamPoints > maxPoints + 10 ||
          set.opponentTeamPoints > maxPoints + 10
        ) {
          errors.push(
            `Match ${index + 1}, Set ${
              setIndex + 1
            }: Points seem unreasonably high for volleyball`
          );
        }

        // Check for proper set completion (must reach minimum points and win by 2)
        if (
          set.userTeamPoints >= maxPoints ||
          set.opponentTeamPoints >= maxPoints
        ) {
          const diff = Math.abs(set.userTeamPoints - set.opponentTeamPoints);
          if (diff < 2) {
            errors.push(
              `Match ${index + 1}, Set ${
                setIndex + 1
              }: Must win by at least 2 points`
            );
          }
        } else if (
          set.userTeamPoints < maxPoints &&
          set.opponentTeamPoints < maxPoints &&
          (set.userTeamPoints > 0 || set.opponentTeamPoints > 0)
        ) {
          // Only validate if there's some scoring activity
          errors.push(
            `Match ${index + 1}, Set ${
              setIndex + 1
            }: At least one team must reach ${maxPoints} points to complete the set`
          );
        }
      });

      // Validate point events
      match.pointEvents.forEach((event, eventIndex) => {
        if (!event.team || !event.pointType) {
          errors.push(
            `Match ${index + 1}, Point Event ${
              eventIndex + 1
            }: Team and point type are required`
          );
        }

        if (event.player) {
          // Check if player belongs to the correct team
          const team =
            event.team === "user" ? match.userTeam : match.opponentTeam;
          const playerExists = team.some(
            (player) => player.name === event.player
          );
          if (!playerExists && event.player !== "Guest Player") {
            errors.push(
              `Match ${index + 1}, Point Event ${eventIndex + 1}: Player "${
                event.player
              }" is not in the ${
                event.team === "user" ? "your" : "opponent"
              } team`
            );
          }
        }
      });

      // Validate substitutions
      match.substitutions.forEach((sub, subIndex) => {
        if (!sub.team || !sub.playerOut || !sub.playerIn) {
          errors.push(
            `Match ${index + 1}, Substitution ${
              subIndex + 1
            }: Team, player out, and player in are required`
          );
        }

        // Check if players belong to the correct team
        const team = sub.team === "user" ? match.userTeam : match.opponentTeam;
        const playerOutExists = team.some(
          (player) => player.name === sub.playerOut
        );
        const playerInExists = team.some(
          (player) => player.name === sub.playerIn
        );

        if (!playerOutExists && sub.playerOut !== "Guest Player") {
          errors.push(
            `Match ${index + 1}, Substitution ${subIndex + 1}: Player out "${
              sub.playerOut
            }" is not in the ${sub.team === "user" ? "your" : "opponent"} team`
          );
        }

        if (!playerInExists && sub.playerIn !== "Guest Player") {
          errors.push(
            `Match ${index + 1}, Substitution ${subIndex + 1}: Player in "${
              sub.playerIn
            }" is not in the ${sub.team === "user" ? "your" : "opponent"} team`
          );
        }
      });

      // Check if match has some scoring
      if (
        match.sets.length > 0 &&
        match.sets.every(
          (set) => set.userTeamPoints === 0 && set.opponentTeamPoints === 0
        )
      ) {
        errors.push(
          `Match ${index + 1}: Match should have some scoring activity`
        );
      }

      // Check match completion based on match type
      const requiredSets = match.matchType === "best_of_3" ? 2 : 3;
      if (
        match.sets.length > 0 &&
        match.userSets < requiredSets &&
        match.opponentSets < requiredSets
      ) {
        errors.push(
          `Match ${
            index + 1
          }: Match incomplete - need ${requiredSets} set wins for ${
            match.matchType
          } match`
        );
      }
    });

    return errors;
  };

  const handleSaveAll = async () => {
    const validationErrors = validateMatches();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: volleyballMatches,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all volleyball matches!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  const handleAddVolleyballMatch = () => {
    const newMatch = {
      matchType: "best_of_5",
      userTeamName: "",
      opponentTeamName: "",
      userTeam: [], // Array of player objects with positions
      opponentTeam: [], // Array of player objects with positions
      sets: [],
      userSets: 0,
      opponentSets: 0,
      pointEvents: [], // Detailed point tracking
      substitutions: [], // Player substitutions
      rotations: [], // Rotation tracking
      winner: null,
    };
    setVolleyballMatches((prev) => [...prev, newMatch]);
  };

  const handleRemoveMatch = (index) => {
    setVolleyballMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setVolleyballMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />

      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            {data.sport} Matches at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed volleyball match information"
            )}
          </p>
        </div>

        <button
          className={classes.addButton}
          onClick={handleAddVolleyballMatch}
        >
          Add New Volleyball Match
        </button>

        {volleyballMatches.length === 0 && (
          <div className={classes.noMatchesMessage}>
            No matches added yet. Click the button above to add your first
            volleyball match.
          </div>
        )}

        {volleyballMatches.map((match, index) => (
          <VolleyballMatchForm
            key={index}
            match={match}
            matchNumber={index + 1}
            availableUsers={availableUsers}
            onUpdate={(updatedMatch) => handleUpdateMatch(index, updatedMatch)}
            onRemove={() => handleRemoveMatch(index)}
          />
        ))}

        {volleyballMatches.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Matches
          </button>
        )}
      </div>
    </>
  );
};

const VolleyballMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);
  const [activeTab, setActiveTab] = useState("basic"); // basic, teams, sets, events

  const volleyballPositions = [
    "Outside Hitter",
    "Middle Blocker",
    "Setter",
    "Opposite Hitter",
    "Libero",
    "Defensive Specialist",
  ];

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    tabContainer: "flex mb-5 border-b border-gray-200 flex-wrap",
    tab: "py-2 px-4 text-sm sm:text-base font-medium border-b-2 border-transparent bg-transparent transition-colors",
    activeTab: "",
    formGroup: "space-y-4",
    label: "font-medium text-gray-700",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    select:
      "px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    teamSection: "bg-white p-5 rounded-lg shadow-sm mb-5",
    playerCard:
      "bg-gray-50 p-3 rounded-lg mb-3 flex justify-between items-center",
    tableWrapper: "w-full overflow-x-auto",
    table:
      "w-full text-sm border-collapse mt-4 rounded-lg overflow-hidden shadow-sm",
    tableHeader:
      "bg-gray-100 text-gray-800 font-semibold p-3 text-left border-b border-gray-200",
    tableCell: "p-3 border-b border-gray-200 bg-white",
    numberInput: "w-20 px-3 py-2 border border-gray-300 rounded text-center",
    scoreCard:
      "flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3",
    scoreBox:
      "text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto",
    scoreLabel: "text-sm text-gray-500 mb-1",
    scoreValue: "text-3xl font-bold text-gray-800",
    winnerBox: "text-center p-4 rounded-lg bg-blue-50 min-w-[120px]",
    eventCard:
      "bg-white p-4 rounded-lg mb-3 border border-gray-200 grid gap-3 items-center grid-cols-1 sm:grid-cols-5",
    deleteButton: "bg-red-500 text-white px-2 py-1 rounded text-xs",
  };

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const tabBaseStyle = {
    padding: "10px 16px",
    fontWeight: 600,
    fontSize: "16px",
    color: "#718096",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const tabActiveStyle = {
    color: brandBlue,
    borderBottomColor: brandBlue,
  };

  // Player management
  const addPlayer = (team, userInfo, customName = "") => {
    const updated = { ...localMatch };
    const player = {
      id: userInfo?.id || userInfo?.user_id || `custom_${Date.now()}`,
      userId: userInfo?.id || userInfo?.user_id || null, // Store userId for display
      name: userInfo?.full_name || customName || "Guest Player",
      isCustom: !userInfo,
      position: "Outside Hitter", // Default position
      number: updated[team].length + 1,
      isLibero: false,
    };

    updated[team].push(player);
    updateParent(updated);
  };

  const removePlayer = (team, playerIndex) => {
    const updated = { ...localMatch };
    updated[team].splice(playerIndex, 1);
    updateParent(updated);
  };

  // Set management
  const handleAddSet = () => {
    const requiredSets = localMatch.matchType === "best_of_3" ? 2 : 3;
    const maxSets = localMatch.matchType === "best_of_3" ? 3 : 5;

    // Check if match is already complete
    if (
      localMatch.userSets >= requiredSets ||
      localMatch.opponentSets >= requiredSets
    ) {
      alert(
        `Match is already complete. A ${localMatch.matchType} match requires ${requiredSets} sets to win.`
      );
      return;
    }

    // Check if we've reached maximum sets
    if (localMatch.sets.length >= maxSets) {
      alert(
        `Cannot add more sets. Maximum ${maxSets} sets allowed for ${localMatch.matchType} match.`
      );
      return;
    }

    const newSet = {
      userTeamPoints: 0,
      opponentTeamPoints: 0,
      setNumber: localMatch.sets.length + 1,
    };
    const updated = {
      ...localMatch,
      sets: [...localMatch.sets, newSet],
    };
    recalculateMatchResults(updated);
  };

  const handleSetChange = (index, field, value) => {
    // Validate and limit input values
    let limitedValue = Math.max(0, Number(value));

    // Determine max points based on set number and match type
    const isDecidingSet =
      (localMatch.matchType === "best_of_3" && index === 2) ||
      (localMatch.matchType === "best_of_5" && index === 4);
    const maxPoints = isDecidingSet ? 15 : 25;

    // Allow some flexibility above the target for deuce situations
    limitedValue = Math.min(limitedValue, maxPoints + 10);

    const updatedSets = localMatch.sets.map((set, i) => {
      if (i === index) {
        return { ...set, [field]: limitedValue };
      }
      return set;
    });
    const updated = { ...localMatch, sets: updatedSets };
    recalculateMatchResults(updated);
  };

  const handleRemoveSet = (index) => {
    const updatedSets = localMatch.sets.filter((_, i) => i !== index);
    const updated = { ...localMatch, sets: updatedSets };
    recalculateMatchResults(updated);
  };

  // Event management
  const addPointEvent = () => {
    const updated = { ...localMatch };
    updated.pointEvents.push({
      id: Date.now(),
      set: Math.max(1, localMatch.sets.length),
      team: "user",
      player: "",
      pointType: "attack", // attack, block, serve, opponent_error
      description: "",
    });
    updateParent(updated);
  };

  const updatePointEvent = (index, field, value) => {
    const updated = { ...localMatch };
    updated.pointEvents[index][field] = value;
    updateParent(updated);
  };

  const removePointEvent = (index) => {
    const updated = { ...localMatch };
    updated.pointEvents.splice(index, 1);
    updateParent(updated);
  };

  const addSubstitution = () => {
    const updated = { ...localMatch };
    updated.substitutions.push({
      id: Date.now(),
      team: "user",
      playerOut: "",
      playerIn: "",
      set: Math.max(1, localMatch.sets.length),
      reason: "tactical", // tactical, injury, rotation
    });
    updateParent(updated);
  };

  const updateSubstitution = (index, field, value) => {
    const updated = { ...localMatch };
    updated.substitutions[index][field] = value;
    updateParent(updated);
  };

  const removeSubstitution = (index) => {
    const updated = { ...localMatch };
    updated.substitutions.splice(index, 1);
    updateParent(updated);
  };

  const recalculateMatchResults = (updatedMatch) => {
    let userSets = 0;
    let opponentSets = 0;

    updatedMatch.sets.forEach((set, index) => {
      // Determine set target based on which set it is
      const isDecidingSet =
        (updatedMatch.matchType === "best_of_3" && index === 2) ||
        (updatedMatch.matchType === "best_of_5" && index === 4);
      const targetPoints = isDecidingSet ? 15 : 25;

      // Volleyball set scoring: first to 25 points (or 15 for deciding set), must win by 2
      if (
        set.userTeamPoints >= targetPoints &&
        set.userTeamPoints >= set.opponentTeamPoints + 2
      ) {
        userSets++;
      } else if (
        set.opponentTeamPoints >= targetPoints &&
        set.opponentTeamPoints >= set.userTeamPoints + 2
      ) {
        opponentSets++;
      }
    });

    updatedMatch.userSets = userSets;
    updatedMatch.opponentSets = opponentSets;

    // Determine match winner
    const requiredSets = updatedMatch.matchType === "best_of_3" ? 2 : 3;

    if (userSets >= requiredSets) {
      updatedMatch.winner = "user";
    } else if (opponentSets >= requiredSets) {
      updatedMatch.winner = "opponent";
    } else {
      updatedMatch.winner = null;
    }

    updateParent(updatedMatch);
  };

  const isMatchComplete = () => {
    const requiredSets = localMatch.matchType === "best_of_3" ? 2 : 3;
    return (
      localMatch.userSets >= requiredSets ||
      localMatch.opponentSets >= requiredSets
    );
  };

  const renderBasicInfo = () => (
    <div>
      {/* Volleyball Scoring Guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5">
        <h4 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
          🏐 Volleyball Match Guide
        </h4>
        <div className="text-blue-700 space-y-2 text-sm leading-relaxed">
          <p>
            <strong>Team Setup:</strong> Each team needs exactly 6 players on
            court (minimum 6 total players)
          </p>
          <p>
            <strong>Match Format:</strong> Best of 5 sets (first team to win 3
            sets)
          </p>
          <p>
            <strong>Set Scoring:</strong> Sets 1-4: First to 25 points (must win
            by 2) | Set 5: First to 15 points (must win by 2)
          </p>
          <p>
            <strong>Libero:</strong> Maximum 2 liberos per team (defensive
            specialists)
          </p>
          <p>
            <strong>Substitutions:</strong> Track player substitutions during
            the match
          </p>
        </div>
      </div>

      <div className={formClasses.formGroup}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Your Team Name</label>
            <input
              type="text"
              value={localMatch.userTeamName}
              onChange={(e) =>
                updateParent({ ...localMatch, userTeamName: e.target.value })
              }
              className={formClasses.input}
              placeholder="Enter your team name"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Opponent Team Name</label>
            <input
              type="text"
              value={localMatch.opponentTeamName}
              onChange={(e) =>
                updateParent({
                  ...localMatch,
                  opponentTeamName: e.target.value,
                })
              }
              className={formClasses.input}
              placeholder="Enter opponent team name"
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-3">
          <label className={formClasses.label}>Match Type</label>
          <select
            value={localMatch.matchType}
            onChange={(e) =>
              updateParent({ ...localMatch, matchType: e.target.value })
            }
            className={`${formClasses.select} w-full max-w-[240px]`}
          >
            <option value="best_of_5">Best of 5 Sets</option>
            <option value="best_of_3">Best of 3 Sets</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderTeamManagement = () => (
    <div>
      {/* User Team */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-2">
          Your Team: {localMatch.userTeamName || "Unnamed Team"} (
          {localMatch.userTeam.length}/6+ players)
        </h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName) =>
            addPlayer("userTeam", userInfo, customName)
          }
          selectedUserIds={[
            ...localMatch.userTeam.filter((p) => p.userId).map((p) => p.userId),
            ...localMatch.opponentTeam
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.userTeam.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <span className="font-medium text-gray-800">
                #{player.number} {player.name} ({player.position}){" "}
                {player.isLibero && "(Libero)"} {player.isCustom && "(Custom)"}
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={player.position}
                  onChange={(e) => {
                    const updated = { ...localMatch };
                    updated.userTeam[index].position = e.target.value;
                    updated.userTeam[index].isLibero =
                      e.target.value === "Libero";
                    updateParent(updated);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  {volleyballPositions.map((pos) => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => removePlayer("userTeam", index)}
                  className={formClasses.deleteButton}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Opponent Team */}
      <div className={formClasses.teamSection}>
        <h4 className="text-lg font-semibold text-gray-800 mb-2">
          Opponent Team: {localMatch.opponentTeamName || "Unnamed Team"} (
          {localMatch.opponentTeam.length}/6+ players)
        </h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName) =>
            addPlayer("opponentTeam", userInfo, customName)
          }
          selectedUserIds={[
            ...localMatch.userTeam.filter((p) => p.userId).map((p) => p.userId),
            ...localMatch.opponentTeam
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.opponentTeam.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <span className="font-medium text-gray-800">
                #{player.number} {player.name} ({player.position}){" "}
                {player.isLibero && "(Libero)"} {player.isCustom && "(Custom)"}
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={player.position}
                  onChange={(e) => {
                    const updated = { ...localMatch };
                    updated.opponentTeam[index].position = e.target.value;
                    updated.opponentTeam[index].isLibero =
                      e.target.value === "Libero";
                    updateParent(updated);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded"
                >
                  {volleyballPositions.map((pos) => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => removePlayer("opponentTeam", index)}
                  className={formClasses.deleteButton}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSets = () => (
    <div>
      <button
        onClick={handleAddSet}
        className={formClasses.addButton}
      >
        Add Set
      </button>

      {localMatch.sets.length > 0 && (
        <div className={formClasses.tableWrapper}>
          <table className={formClasses.table}>
            <thead>
              <tr>
                <th className={formClasses.tableHeader}>Set</th>
                <th className={formClasses.tableHeader}>
                  {localMatch.userTeamName || "Your Team"} Points
                </th>
                <th className={formClasses.tableHeader}>
                  {localMatch.opponentTeamName || "Opponent"} Points
                </th>
                <th className={formClasses.tableHeader}>Target</th>
                <th className={formClasses.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {localMatch.sets.map((set, i) => {
                const isDecidingSet =
                  (localMatch.matchType === "best_of_3" && i === 2) ||
                  (localMatch.matchType === "best_of_5" && i === 4);
                const targetPoints = isDecidingSet ? 15 : 25;

                return (
                  <tr key={i}>
                    <td
                      className={`${formClasses.tableCell} font-semibold bg-gray-50`}
                    >
                      Set {i + 1}
                    </td>
                    <td className={formClasses.tableCell}>
                      <input
                        type="number"
                        value={set.userTeamPoints}
                        onChange={(e) =>
                          handleSetChange(i, "userTeamPoints", e.target.value)
                        }
                        min="0"
                        className={formClasses.numberInput}
                      />
                    </td>
                    <td className={formClasses.tableCell}>
                      <input
                        type="number"
                        value={set.opponentTeamPoints}
                        onChange={(e) =>
                          handleSetChange(i, "opponentTeamPoints", e.target.value)
                        }
                        min="0"
                        className={formClasses.numberInput}
                      />
                    </td>
                    <td
                      className={`${formClasses.tableCell} font-semibold text-gray-500`}
                    >
                      {targetPoints} pts {isDecidingSet && "(Deciding)"}
                    </td>
                    <td className={formClasses.tableCell}>
                      <button
                        onClick={() => handleRemoveSet(i)}
                        className={formClasses.deleteButton}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderEvents = () => (
    <div>
      {/* Point Events */}
      <div className={formClasses.teamSection}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Point Events</h4>
          <button onClick={addPointEvent} className={formClasses.addButton}>
            Add Point
          </button>
        </div>
        {localMatch.pointEvents.map((event, index) => (
          <div key={event.id} className={formClasses.eventCard}>
            <select
              value={event.team}
              onChange={(e) => updatePointEvent(index, "team", e.target.value)}
              className={formClasses.select}
            >
              <option value="user">
                {localMatch.userTeamName || "Your Team"}
              </option>
              <option value="opponent">
                {localMatch.opponentTeamName || "Opponent"}
              </option>
            </select>
            <select
              value={event.player}
              onChange={(e) =>
                updatePointEvent(index, "player", e.target.value)
              }
              className={formClasses.input}
            >
              <option value="">Select player</option>
              {event.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={event.pointType}
              onChange={(e) =>
                updatePointEvent(index, "pointType", e.target.value)
              }
              className={formClasses.select}
            >
              <option value="attack">Attack/Spike</option>
              <option value="block">Block</option>
              <option value="serve">Serve Ace</option>
              <option value="opponent_error">Opponent Error</option>
              <option value="tip">Tip/Dink</option>
            </select>
            <select
              value={event.set}
              onChange={(e) =>
                updatePointEvent(index, "set", Number(e.target.value))
              }
              className={`${formClasses.select} min-w-[70px]`}
            >
              {[...Array(Math.max(1, localMatch.sets.length))].map((_, i) => (
                <option key={i} value={i + 1}>
                  Set {i + 1}
                </option>
              ))}
            </select>
            <button
              onClick={() => removePointEvent(index)}
              className={formClasses.deleteButton}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      {/* Substitutions */}
      <div className={formClasses.teamSection}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">Substitutions</h4>
          <button onClick={addSubstitution} className={formClasses.addButton}>
            Add Substitution
          </button>
        </div>
        {localMatch.substitutions.map((sub, index) => (
          <div key={sub.id} className={formClasses.eventCard}>
            <select
              value={sub.team}
              onChange={(e) =>
                updateSubstitution(index, "team", e.target.value)
              }
              className={formClasses.select}
            >
              <option value="user">
                {localMatch.userTeamName || "Your Team"}
              </option>
              <option value="opponent">
                {localMatch.opponentTeamName || "Opponent"}
              </option>
            </select>
            <select
              value={sub.playerOut}
              onChange={(e) =>
                updateSubstitution(index, "playerOut", e.target.value)
              }
              className={formClasses.input}
            >
              <option value="">Player going out</option>
              {sub.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={sub.playerIn}
              onChange={(e) =>
                updateSubstitution(index, "playerIn", e.target.value)
              }
              className={formClasses.input}
            >
              <option value="">Player coming in</option>
              {sub.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={sub.set}
              onChange={(e) =>
                updateSubstitution(index, "set", Number(e.target.value))
              }
              className={`${formClasses.select} min-w-[70px]`}
            >
              {[...Array(Math.max(1, localMatch.sets.length))].map((_, i) => (
                <option key={i} value={i + 1}>
                  Set {i + 1}
                </option>
              ))}
            </select>
            <button
              onClick={() => removeSubstitution(index)}
              className={formClasses.deleteButton}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Volleyball Match #{matchNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Match
        </button>
      </div>

      <div className={formClasses.tabContainer}>
        {["basic", "teams", "sets", "events"].map((tab) => (
          <button
            key={tab}
            className={formClasses.tab}
            style={{
              ...tabBaseStyle,
              ...(activeTab === tab ? tabActiveStyle : {}),
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "basic"
              ? "Basic Info"
              : tab === "teams"
              ? "Teams"
              : tab === "sets"
              ? "Sets"
              : "Events"}
          </button>
        ))}
      </div>

      {activeTab === "basic" && renderBasicInfo()}
      {activeTab === "teams" && renderTeamManagement()}
      {activeTab === "sets" && renderSets()}
      {activeTab === "events" && renderEvents()}

      {/* Final Score Display */}
      <div className={formClasses.scoreCard}>
        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.userTeamName || "Your Team"} Sets
          </div>
          <div className={formClasses.scoreValue}>
            {localMatch.userSets}
          </div>
        </div>

        <div className={formClasses.winnerBox}>
          <div className={formClasses.scoreLabel}>
            {isMatchComplete() ? "Winner" : "Match Status"}
          </div>
          <div className="text-base font-semibold text-blue-800">
            {localMatch.winner === null
              ? "In Progress"
              : localMatch.winner === "user"
              ? localMatch.userTeamName || "Your Team"
              : localMatch.opponentTeamName || "Opponent"}
          </div>
        </div>

        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.opponentTeamName || "Opponent"} Sets
          </div>
          <div className={formClasses.scoreValue}>
            {localMatch.opponentSets}
          </div>
        </div>
      </div>

      {/* Info about volleyball scoring */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-sm text-blue-700">
        <strong>Volleyball Scoring:</strong> Sets 1-4 play to 25 points (must
        win by 2). Deciding set (if needed) plays to 15 points (must win by 2).
      </div>
    </div>
  );
};

// PlayerSelector component with search functionality
const PlayerSelector = ({
  availableUsers,
  onAddPlayer,
  selectedUserIds = [],
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAdd = () => {
    if (useCustomName && customName.trim()) {
      onAddPlayer(null, customName.trim());
      setCustomName("");
    } else if (!useCustomName && selectedUser) {
      onAddPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
    }
  };

  const ps = {
    container: "flex flex-wrap gap-3 items-center relative",
    searchContainer: "relative flex-1 min-w-[250px]",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm",
    suggestionsDropdown:
      "absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-[200px] overflow-y-auto z-[1000] shadow-lg",
    suggestionItem:
      "px-3 py-2 border-b border-gray-100 transition-colors text-sm",
    button:
      "bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm",
    toggle: "text-blue-600 hover:text-blue-800 text-sm underline",
    noResults: "px-3 py-2 text-sm text-gray-600 text-center",
  };

  return (
    <div className={ps.container}>
      {!useCustomName ? (
        <>
          <div className={ps.searchContainer}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search for system users..."
              className={ps.input}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className={ps.suggestionsDropdown}>
                {suggestions.map((user) => {
                  const isAlreadySelected = selectedUserIds.includes(
                    user.id || user.user_id
                  );
                  return (
                    <div
                      key={user.id || user.user_id}
                      className={`${ps.suggestionItem} ${
                        isAlreadySelected
                          ? "opacity-50 cursor-not-allowed bg-gray-100"
                          : "cursor-pointer hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        !isAlreadySelected && handleSelectUser(user)
                      }
                    >
                      <div className="font-medium">
                        {user.full_name || user.username}
                        {isAlreadySelected && (
                          <span className="text-red-500 ml-2">
                            (Already Added)
                          </span>
                        )}
                      </div>
                      {user.email && (
                        <div className="text-xs text-gray-600">
                          {user.email}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {showSuggestions &&
              searchTerm.length >= 2 &&
              suggestions.length === 0 && (
                <div className={ps.suggestionsDropdown}>
                  <div className={ps.noResults}>No users found</div>
                </div>
              )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!selectedUser}
            className={`${ps.button} ${
              selectedUser ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add Player
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Enter custom player name"
            className={ps.input}
          />
          <button
            onClick={handleAdd}
            disabled={!customName.trim()}
            className={`${ps.button} ${
              customName.trim() ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add Player
          </button>
        </>
      )}
      <span
        onClick={() => setUseCustomName(!useCustomName)}
        className={ps.toggle}
      >
        {useCustomName ? "Use system users" : "Add custom name"}
      </span>
    </div>
  );
};

export default AddVolleyball;
